import React, { useState, useEffect, useCallback } from "react";
import {
  collection, getDocs, addDoc, setDoc, deleteDoc,
  doc, query, orderBy, getDoc,
} from "firebase/firestore";
import { db } from "../firebase/firebase-config";

/* ── CONSTANTS ──────────────────────────────────────────────────── */
const SECTION_TYPES  = ["value","fact","interest","quote","passion"];
const SECTION_ICONS  = [
  "FaHeart","FaLightbulb","FaStar","FaBookOpen","FaGlobeAmericas",
  "FaMusic","FaPalette","FaCode","FaLeaf","FaCompass",
  "FaFeather","FaMountain","FaSmile","FaGraduationCap","FaCoffee","FaCamera",
];
const COLOR_PRESETS = ["#C25B2A","#D4AF37","#8B4513","#CD7F32","#6B4226","#5B7E91","#7A8C3E"];

const SEED_SECTIONS = [
  { order:1, type:"value",    title:"Precision with Empathy",          body:"I believe the best contracts are written not just with legal precision, but with genuine understanding of each party's needs and concerns.",              iconKey:"FaHeart",          accentColor:"#C25B2A", bgColor:"#fdf8f4" },
  { order:2, type:"passion",  title:"Legal Technology",                 body:"Fascinated by how AI, CLM tools, and automation are reshaping contract management — I stay ahead of the curve so my clients don't have to.",               iconKey:"FaLightbulb",      accentColor:"#D4AF37", bgColor:"#fdf8f4" },
  { order:3, type:"interest", title:"Reading & Research",               body:"From legal journals to fiction, I believe reading broadly makes you a sharper thinker — and a better negotiator.",                                          iconKey:"FaBookOpen",       accentColor:"#8B4513", bgColor:"#fdf8f4" },
  { order:4, type:"fact",     title:"Global Citizen",                   body:"Having studied in India and the UK, and worked with clients across Europe and North America, I bring a genuinely international perspective.",                iconKey:"FaGlobeAmericas",  accentColor:"#C25B2A", bgColor:"#fdf8f4" },
  { order:5, type:"value",    title:"Continuous Learning",              body:"Every contract teaches me something new. I invest in certifications, conferences, and peer learning to stay at the cutting edge of contract management.", iconKey:"FaGraduationCap",  accentColor:"#D4AF37", bgColor:"#fdf8f4" },
  { order:6, type:"interest", title:"Travel & New Experiences",         body:"Exploring new places sharpens my curiosity and perspective — qualities I bring directly into my legal and consulting work.",                              iconKey:"FaCompass",        accentColor:"#8B4513", bgColor:"#fdf8f4" },
];

const DEFAULT_PROFILE = {
  heroTitle:      "Get to Know Me",
  heroSubtitle:   "The person behind the practice — curious, driven, and deeply human",
  introHeading:   "The Person Behind the Practice",
  introParagraph1:"Beyond contracts and compliance, I'm someone who believes that great legal work starts with genuine curiosity and ends with real-world impact.",
  introParagraph2:"I bring the same precision and care to my relationships as I do to my contracts — whether I'm mentoring a junior lawyer, exploring a new city, or getting lost in a good book.",
  heroImageUrl:   "",
  accentColor1:   "#C25B2A",
  accentColor2:   "#D4AF37",
  bgColor:        "#fdf8f4",
  sectionTitle:   "What Makes Me, Me",
  ctaText:        "Let's work together",
  ctaLink:        "/work-with-me",
};

const DEFAULT_SECTION = {
  order:1, type:"value", title:"", body:"",
  iconKey:"FaHeart", accentColor:"#C25B2A", bgColor:"#fdf8f4",
};

/* ── COLOUR ROW ─────────────────────────────────────────────────── */
function ColorRow({ value, onChange }) {
  return (
    <div className="adm-color-row">
      {COLOR_PRESETS.map(c => (
        <button key={c} type="button"
          className={`adm-color-swatch ${value === c ? "active" : ""}`}
          style={{ background:c }} onClick={() => onChange(c)} />
      ))}
      <input type="color" value={value || "#C25B2A"} onChange={e => onChange(e.target.value)} />
    </div>
  );
}

/* ── PROFILE FORM ───────────────────────────────────────────────── */
function ProfileForm({ form, setForm, onSave, onCancel, saving }) {
  const f = form;
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <div className="adm-skill-form">
      <h3 className="adm-form-heading">Edit Profile</h3>

      <h4 className="adm-form-subheading">Hero Section</h4>
      <div className="adm-form-grid">
        <label><span>Hero Title</span>
          <input type="text" value={f.heroTitle || ""} onChange={e => set("heroTitle", e.target.value)} />
        </label>
        <label><span>Hero Image URL</span>
          <input type="text" value={f.heroImageUrl || ""} onChange={e => set("heroImageUrl", e.target.value)} placeholder="Paste URL from Media Library" />
        </label>
        <label style={{ gridColumn:"1/-1" }}><span>Hero Subtitle</span>
          <textarea value={f.heroSubtitle || ""} onChange={e => set("heroSubtitle", e.target.value)} rows={2} />
        </label>
      </div>

      <h4 className="adm-form-subheading">Introduction</h4>
      <div className="adm-form-grid">
        <label style={{ gridColumn:"1/-1" }}><span>Intro Heading</span>
          <input type="text" value={f.introHeading || ""} onChange={e => set("introHeading", e.target.value)} />
        </label>
        <label style={{ gridColumn:"1/-1" }}><span>Paragraph 1</span>
          <textarea value={f.introParagraph1 || ""} onChange={e => set("introParagraph1", e.target.value)} rows={3} />
        </label>
        <label style={{ gridColumn:"1/-1" }}><span>Paragraph 2</span>
          <textarea value={f.introParagraph2 || ""} onChange={e => set("introParagraph2", e.target.value)} rows={3} />
        </label>
        <label><span>Cards Section Title</span>
          <input type="text" value={f.sectionTitle || ""} onChange={e => set("sectionTitle", e.target.value)} />
        </label>
      </div>

      <h4 className="adm-form-subheading">Design & Colors</h4>
      <div className="adm-form-grid">
        <label><span>Accent Color 1</span>
          <ColorRow value={f.accentColor1} onChange={v => set("accentColor1", v)} />
        </label>
        <label><span>Accent Color 2</span>
          <ColorRow value={f.accentColor2} onChange={v => set("accentColor2", v)} />
        </label>
        <label><span>Page Background</span>
          <input type="color" value={f.bgColor || "#fdf8f4"} onChange={e => set("bgColor", e.target.value)} />
        </label>
      </div>

      <h4 className="adm-form-subheading">CTA Button</h4>
      <div className="adm-form-grid">
        <label><span>Button Text</span>
          <input type="text" value={f.ctaText || ""} onChange={e => set("ctaText", e.target.value)} />
        </label>
        <label><span>Button Link</span>
          <input type="text" value={f.ctaLink || ""} onChange={e => set("ctaLink", e.target.value)} />
        </label>
      </div>

      <div className="adm-form-actions">
        <button className="adm-btn adm-btn-primary" onClick={() => onSave(f)} disabled={saving}>
          {saving ? "Saving…" : "Save Profile"}
        </button>
        <button className="adm-btn" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

/* ── SECTION FORM ───────────────────────────────────────────────── */
function SectionForm({ form, setForm, onSave, onCancel, saving }) {
  const f = form;
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <div className="adm-skill-form">
      <h3 className="adm-form-heading">{f.id ? "Edit Card" : "Add Card"}</h3>
      <div className="adm-form-grid">
        <label><span>Order</span>
          <input type="number" value={f.order} onChange={e => set("order", e.target.value)} min={1} />
        </label>
        <label><span>Type</span>
          <select value={f.type} onChange={e => set("type", e.target.value)}>
            {SECTION_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase()+t.slice(1)}</option>)}
          </select>
        </label>
        <label><span>Title *</span>
          <input type="text" value={f.title} onChange={e => set("title", e.target.value)} />
        </label>
        <label><span>Icon</span>
          <select value={f.iconKey} onChange={e => set("iconKey", e.target.value)}>
            {SECTION_ICONS.map(k => <option key={k} value={k}>{k}</option>)}
          </select>
        </label>
        <label style={{ gridColumn:"1/-1" }}><span>Body text</span>
          <textarea value={f.body} onChange={e => set("body", e.target.value)} rows={3} />
        </label>
        <label><span>Accent Color</span>
          <ColorRow value={f.accentColor} onChange={v => set("accentColor", v)} />
        </label>
        <label><span>Card Background</span>
          <input type="color" value={f.bgColor || "#fdf8f4"} onChange={e => set("bgColor", e.target.value)} />
        </label>
      </div>
      <div className="adm-form-actions">
        <button className="adm-btn adm-btn-primary" onClick={() => onSave(f)} disabled={saving || !f.title.trim()}>
          {saving ? "Saving…" : f.id ? "Update" : "Add Card"}
        </button>
        <button className="adm-btn" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

/* ── MAIN PANEL ─────────────────────────────────────────────────── */
export default function AboutPanel() {
  const [profile,     setProfile]     = useState(null);
  const [sections,    setSections]    = useState([]);
  const [tab,         setTab]         = useState("profile");
  const [loading,     setLoading]     = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [seeding,     setSeeding]     = useState(false);
  const [profileForm, setProfileForm] = useState(null);
  const [sectionForm, setSectionForm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [profileSnap, sectionsSnap] = await Promise.all([
        getDoc(doc(db, "about_profile", "main")),
        getDocs(query(collection(db, "about_sections"), orderBy("order","asc"))),
      ]);
      setProfile(profileSnap.exists() ? profileSnap.data() : DEFAULT_PROFILE);
      setSections(sectionsSnap.docs.map(d => ({ id:d.id, ...d.data() })));
    } catch(e) { console.error(e); }
    finally    { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveProfile = async (form) => {
    setSaving(true);
    try {
      await setDoc(doc(db,"about_profile","main"), form);
      setProfileForm(null); await load();
    } finally { setSaving(false); }
  };

  const saveSection = async (form) => {
    setSaving(true);
    try {
      const { id, ...data } = form;
      const clean = { ...data, order:Number(data.order) };
      if (id) await setDoc(doc(db,"about_sections",id), clean);
      else    await addDoc(collection(db,"about_sections"), clean);
      setSectionForm(null); await load();
    } finally { setSaving(false); }
  };

  const deleteSection = async (id) => {
    if (!window.confirm("Delete this card?")) return;
    await deleteDoc(doc(db,"about_sections",id)); await load();
  };

  const seedAll = async () => {
    if (!window.confirm("Seed default 'About Me' sections to Firestore?")) return;
    setSeeding(true);
    try {
      await Promise.all([
        setDoc(doc(db,"about_profile","main"), DEFAULT_PROFILE),
        ...SEED_SECTIONS.map(s => addDoc(collection(db,"about_sections"), s)),
      ]);
      await load();
    } catch(e) { alert("Seed error: " + e.message); }
    finally    { setSeeding(false); }
  };

  if (loading && !profile) return <div className="adm-panel-loading">Loading About data…</div>;

  return (
    <section className="adm-panel">
      <div className="adm-panel-head">
        <div>
          <h2>Get to Know Me</h2>
          <p>Manage the personal profile page — content and design</p>
        </div>
        <div className="adm-panel-head-right">
          {sections.length === 0 && (
            <button className="adm-btn adm-btn-seed" onClick={seedAll} disabled={seeding}>
              {seeding ? "Seeding…" : "Seed Default Data"}
            </button>
          )}
        </div>
      </div>

      <div className="adm-tab-row">
        {[["profile","Profile & Design"],["sections","Section Cards"]].map(([v,l]) => (
          <button key={v} className={`adm-tab-btn ${tab===v?"active":""}`} onClick={() => setTab(v)}>{l}</button>
        ))}
      </div>

      {/* ── PROFILE TAB ── */}
      {tab === "profile" && (
        <div className="adm-tab-content">
          {profileForm
            ? <ProfileForm form={profileForm} setForm={setProfileForm} onSave={saveProfile} onCancel={() => setProfileForm(null)} saving={saving} />
            : (
              <div className="adm-config-preview">
                <div className="adm-config-row"><strong>Hero Title</strong><span>{profile?.heroTitle}</span></div>
                <div className="adm-config-row"><strong>Intro Heading</strong><span>{profile?.introHeading}</span></div>
                <div className="adm-config-row"><strong>Cards Section Title</strong><span>{profile?.sectionTitle}</span></div>
                <div className="adm-config-row">
                  <strong>Accent Colors</strong>
                  <span>
                    <span className="adm-color-dot" style={{ background:profile?.accentColor1 }} />{profile?.accentColor1}
                    &nbsp;&amp;&nbsp;
                    <span className="adm-color-dot" style={{ background:profile?.accentColor2 }} />{profile?.accentColor2}
                  </span>
                </div>
                <div className="adm-config-row"><strong>CTA</strong><span>{profile?.ctaText} → {profile?.ctaLink}</span></div>
                <button className="adm-btn adm-btn-primary" onClick={() => setProfileForm({ ...DEFAULT_PROFILE, ...profile })}>
                  Edit Profile
                </button>
              </div>
            )
          }
        </div>
      )}

      {/* ── SECTIONS TAB ── */}
      {tab === "sections" && (
        <div className="adm-tab-content">
          <div className="adm-panel-toolbar">
            <button className="adm-btn adm-btn-primary"
              onClick={() => setSectionForm({ ...DEFAULT_SECTION, order:sections.length+1 })}>
              + Add Card
            </button>
            <span className="adm-count-badge">{sections.length} cards</span>
          </div>
          {sectionForm && <SectionForm form={sectionForm} setForm={setSectionForm} onSave={saveSection} onCancel={() => setSectionForm(null)} saving={saving} />}
          <div className="adm-list">
            {sections.length === 0 && <p className="adm-empty">No cards yet — click "Seed Default Data" in the header or add manually.</p>}
            {sections.map(s => (
              <div key={s.id} className="adm-list-row" style={{ "--row-color": s.accentColor }}>
                <div className="adm-list-row-left">
                  <div className="adm-list-order">{s.order}</div>
                  <div>
                    <div className="adm-list-title">{s.title}</div>
                    <div className="adm-list-meta">{s.type} · {s.iconKey}</div>
                  </div>
                </div>
                <div className="adm-list-actions">
                  <button className="adm-btn adm-btn-sm" onClick={() => setSectionForm(s)}>Edit</button>
                  <button className="adm-btn adm-btn-sm adm-btn-danger" onClick={() => deleteSection(s.id)}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
